import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'

import store from 'store'

class RedesignSearchOverlay {
  constructor (root) {
    this.root = root
    this.headerRoot = document.querySelector('.redesign-header')
    this.panel = this.root.querySelector('.redesign-search__panel')
    this.mobileTrigger = document.querySelector('[data-mobile-nav-trigger]')
    this.mobileSearchTriggers = Array.from(document.querySelectorAll('[data-trigger="search"]'))
    this.desktopTriggers = Array.from(document.querySelectorAll('.redesign-header__utility-link'))
      .filter((trigger) => trigger.tagName === 'BUTTON')
    this.inputs = Array.from(this.root.querySelectorAll('[data-redesign-search-input]'))
    this.emptyStates = Array.from(this.root.querySelectorAll('[data-redesign-search-empty-state]'))
    this.resultStates = Array.from(this.root.querySelectorAll('[data-redesign-search-result-state]'))
    this.suggestionLists = Array.from(this.root.querySelectorAll('[data-redesign-search-suggestions]'))
    this.predictiveGroups = Array.from(this.root.querySelectorAll('[data-redesign-search-predictive-group]'))
    this.collectionGroups = Array.from(this.root.querySelectorAll('[data-redesign-search-collections-group]'))
    this.collectionLists = Array.from(this.root.querySelectorAll('[data-redesign-search-collections]'))
    this.cardsContainers = Array.from(this.root.querySelectorAll('[data-redesign-search-cards]'))
    this.emptyCards = Array.from(this.root.querySelectorAll('[data-redesign-search-empty-cards]'))
    this.noResultsMessages = Array.from(this.root.querySelectorAll('[data-redesign-search-no-results-message]'))
    this.mobileHomeBreadcrumb = this.root.querySelector('[data-redesign-search-home]')
    this.mediaQuery = window.matchMedia('(min-width: 1280px)')
    this.viewportIsDesktop = this.mediaQuery.matches
    this.query = ''
    this.isOpen = false
    this.requestIndex = 0
    this.debounceTimer = null
    this.hideTimer = null
    this.focusTimer = null
    this.viewportWatchFrame = null
    this.previouslyFocused = null
    this.restoreFocusOnClose = true
    this.onInput = this.onInput.bind(this)
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onOverlayOpen = this.onOverlayOpen.bind(this)
    this.onViewportChange = this.onViewportChange.bind(this)
    this.onSearchStateChange = this.onSearchStateChange.bind(this)
    this.onMobileHomeClick = this.onMobileHomeClick.bind(this)
    this.onMediaChange = this.onMediaChange.bind(this)
  }

  init () {
    if (!this.root || this.inputs.length === 0) return

    this.inputs.forEach((input) => {
      input.addEventListener('input', this.onInput)
    })

    if (this.mobileHomeBreadcrumb) {
      this.mobileHomeBreadcrumb.addEventListener('click', this.onMobileHomeClick)
    }

    document.addEventListener('keydown', this.onDocumentKeydown)
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('redesign:overlay-open', this.onOverlayOpen)
    window.addEventListener('resize', this.onViewportChange)
    window.addEventListener('scroll', this.onViewportChange, { passive: true })

    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', this.onMediaChange)
    } else if (typeof this.mediaQuery.addListener === 'function') {
      this.mediaQuery.addListener(this.onMediaChange)
    }

    this.unwatch = store.watch(
      (state) => state.global.searchOpened,
      this.onSearchStateChange
    )

    this.onSearchStateChange(store.state.global.searchOpened)
  }

  // Same page lock as the cart drawer: `.no-scroll` on html/body, plus body-scroll-lock for iOS
  setPageScrollLock (locked) {
    document.documentElement.classList.toggle('no-scroll', locked)
    document.body.classList.toggle('no-scroll', locked)

    if (locked) {
      if (this.panel) {
        disableBodyScroll(this.panel)
      }

      return
    }

    clearAllBodyScrollLocks()

    if (this.panel) {
      enableBodyScroll(this.panel)
    }
  }

  destroy () {
    this.setPageScrollLock(false)

    window.clearTimeout(this.debounceTimer)
    window.clearTimeout(this.hideTimer)
    window.clearTimeout(this.focusTimer)
    window.cancelAnimationFrame(this.viewportWatchFrame)

    this.inputs.forEach((input) => {
      input.removeEventListener('input', this.onInput)
    })

    if (this.mobileHomeBreadcrumb) {
      this.mobileHomeBreadcrumb.removeEventListener('click', this.onMobileHomeClick)
    }

    document.removeEventListener('keydown', this.onDocumentKeydown)
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('redesign:overlay-open', this.onOverlayOpen)
    window.removeEventListener('resize', this.onViewportChange)
    window.removeEventListener('scroll', this.onViewportChange)

    if (typeof this.mediaQuery.removeEventListener === 'function') {
      this.mediaQuery.removeEventListener('change', this.onMediaChange)
    } else if (typeof this.mediaQuery.removeListener === 'function') {
      this.mediaQuery.removeListener(this.onMediaChange)
    }

    if (typeof this.unwatch === 'function') {
      this.unwatch()
    }
  }

  onSearchStateChange (isOpen) {
    if (isOpen) {
      this.open()
      return
    }

    this.close()
  }

  open () {
    if (this.isOpen) {
      this.updateViewportMeasurements()
      this.syncTriggerState()
      return
    }

    this.isOpen = true
    this.restoreFocusOnClose = true
    this.previouslyFocused = document.activeElement
    this.viewportIsDesktop = this.mediaQuery.matches
    this.updateViewportMeasurements()
    window.clearTimeout(this.hideTimer)
    this.root.hidden = false

    window.requestAnimationFrame(() => {
      if (!this.isOpen) return

      this.root.setAttribute('aria-hidden', 'false')
      this.queueInputFocus()
    })

    this.setPageScrollLock(true)
    document.body.classList.add('redesign-search-open')

    if (this.headerRoot) {
      this.headerRoot.classList.add('is-search-open')
    }

    this.startViewportWatch()
    this.syncTriggerState()
    document.dispatchEvent(new CustomEvent('redesign:search-state-change', {
      detail: { isOpen: true }
    }))
  }

  close (options = {}) {
    const restoreFocus = typeof options.restoreFocus === 'undefined'
      ? this.restoreFocusOnClose
      : options.restoreFocus
    const wasOpen = this.isOpen

    this.isOpen = false
    window.clearTimeout(this.focusTimer)
    window.cancelAnimationFrame(this.viewportWatchFrame)
    this.viewportWatchFrame = null
    this.root.setAttribute('aria-hidden', 'true')
    this.setPageScrollLock(false)
    document.body.classList.remove('redesign-search-open')

    if (this.headerRoot) {
      this.headerRoot.classList.remove('is-search-open')
    }

    this.syncTriggerState()
    document.dispatchEvent(new CustomEvent('redesign:search-state-change', {
      detail: { isOpen: false }
    }))

    window.clearTimeout(this.hideTimer)
    this.hideTimer = window.setTimeout(() => {
      if (this.root.getAttribute('aria-hidden') === 'true') {
        this.root.hidden = true
      }
    }, 220)

    this.resetSearchState()
    this.restoreFocusOnClose = true

    if (restoreFocus && wasOpen && this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus()
    }
  }

  onInput (event) {
    const inputValue = event.target.value
    const query = inputValue.trim()

    this.query = inputValue
    this.syncInputValues(inputValue, event.target)
    window.clearTimeout(this.debounceTimer)

    if (query.length < 3) {
      this.requestIndex += 1
      this.showEmptyState()
      return
    }

    const requestIndex = ++this.requestIndex

    this.debounceTimer = window.setTimeout(() => {
      store.dispatch('global/searchItems', query)
        .then((response) => {
          if (requestIndex !== this.requestIndex) return

          this.renderResults(response)
        })
        .catch(() => {
          if (requestIndex !== this.requestIndex) return

          this.renderResults({ resources: { results: {} } })
        })
    }, 150)
  }

  onDocumentKeydown (event) {
    if (!this.isOpen) return

    if (event.key === 'Escape') {
      event.preventDefault()
      this.closeSearch()
      return
    }

    if (event.key === 'Tab') {
      this.trapFocus(event)
    }
  }

  onDocumentClick (event) {
    if (!this.isOpen) return

    const target = event.target
    const clickedInsidePanel = this.panel && this.panel.contains(target)
    const clickedDesktopTrigger = this.desktopTriggers.some((trigger) => trigger.contains(target))
    const clickedMobileTrigger = this.mobileTrigger && this.mobileTrigger.contains(target)
    const clickedMobileSearchTrigger = this.mobileSearchTriggers.some((trigger) => trigger.contains(target))

    if (clickedInsidePanel || clickedDesktopTrigger || clickedMobileTrigger || clickedMobileSearchTrigger) return

    this.closeSearch({ restoreFocus: false })
  }

  onOverlayOpen (event) {
    if (!event.detail || event.detail.source === 'search' || !this.isOpen) return

    this.closeSearch({ restoreFocus: false })
  }

  onMobileHomeClick (event) {
    event.preventDefault()

    document.dispatchEvent(new CustomEvent('redesign:mobile-nav-open-level', {
      detail: { level: 1, restoreFocus: false }
    }))

    this.closeSearch({ restoreFocus: false })
  }

  onViewportChange () {
    const viewportIsDesktop = this.mediaQuery.matches
    const breakpointChanged = viewportIsDesktop !== this.viewportIsDesktop

    this.viewportIsDesktop = viewportIsDesktop

    if (!this.isOpen && !document.body.classList.contains('redesign-search-open')) return

    if (breakpointChanged) {
      this.forceCloseSearch({ restoreFocus: false })
      return
    }

    if (!this.isOpen) return

    this.updateViewportMeasurements()
  }

  onMediaChange () {
    if (!this.isOpen) return

    this.forceCloseSearch({ restoreFocus: false })
  }

  startViewportWatch () {
    window.cancelAnimationFrame(this.viewportWatchFrame)

    const watchViewport = () => {
      if (!this.isOpen) return

      const viewportIsDesktop = this.mediaQuery.matches

      if (viewportIsDesktop !== this.viewportIsDesktop) {
        this.viewportIsDesktop = viewportIsDesktop
        this.forceCloseSearch({ restoreFocus: false })
        return
      }

      this.viewportWatchFrame = window.requestAnimationFrame(watchViewport)
    }

    this.viewportWatchFrame = window.requestAnimationFrame(watchViewport)
  }

  forceCloseSearch (options = {}) {
    const searchOpened = Boolean(store && store.state && store.state.global && store.state.global.searchOpened)

    if (searchOpened) {
      this.closeSearch(options)
    }

    if (this.isOpen || document.body.classList.contains('redesign-search-open')) {
      this.close(options)
    }
  }

  closeSearch (options = {}) {
    if (!store || !store.state || !store.state.global || !store.state.global.searchOpened) return

    this.restoreFocusOnClose = typeof options.restoreFocus === 'undefined' ? true : options.restoreFocus
    store.commit('global/closeSearch')
  }

  queueInputFocus () {
    window.clearTimeout(this.focusTimer)

    this.focusTimer = window.setTimeout(() => {
      if (!this.isOpen) return

      this.focusActiveInput()
    }, 0)
  }

  syncTriggerState () {
    this.desktopTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false')
    })

    if (!this.mobileTrigger) return

    const openLabel = this.mobileTrigger.dataset.mobileNavOpenLabel || 'Open menu'
    const searchCloseLabel = this.mobileTrigger.dataset.searchCloseLabel || 'Close search'

    this.mobileTrigger.classList.toggle('is-search-active', this.isOpen)

    if (this.isOpen) {
      this.mobileTrigger.setAttribute('aria-label', searchCloseLabel)
      return
    }

    if (!this.mobileTrigger.classList.contains('is-active')) {
      this.mobileTrigger.setAttribute('aria-label', openLabel)
    }
  }

  focusActiveInput () {
    const desktopInput = this.inputs[0]
    const mobileInput = this.inputs[this.inputs.length - 1]
    const targetInput = this.mediaQuery.matches ? desktopInput : mobileInput

    if (!targetInput || typeof targetInput.focus !== 'function') return

    targetInput.focus({ preventScroll: true })
    targetInput.select()
  }

  trapFocus (event) {
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    const activeElement = document.activeElement

    if (event.shiftKey) {
      if (activeElement === firstElement || !this.root.contains(activeElement)) {
        event.preventDefault()
        lastElement.focus()
      }

      return
    }

    if (activeElement === lastElement || !this.root.contains(activeElement)) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  getFocusableElements () {
    if (!this.panel) return []

    return Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true')
  }

  updateViewportMeasurements () {
    if (!this.headerRoot) return

    const headerRect = this.headerRoot.getBoundingClientRect()
    const offset = Math.max(headerRect.bottom, 0)
    const height = Math.max(window.innerHeight - offset, 0)

    this.root.style.setProperty('--redesign-search-offset', ''.concat(offset, 'px'))
    this.root.style.setProperty('--redesign-search-height', ''.concat(height, 'px'))
  }

  syncInputValues (value, sourceInput) {
    this.inputs.forEach((input) => {
      if (input === sourceInput) return

      input.value = value
    })
  }

  resetSearchState () {
    this.query = ''
    this.syncInputValues('', null)
    this.inputs.forEach((input) => {
      input.value = ''
    })
    this.showEmptyState()
  }

  showEmptyState () {
    this.root.setAttribute('data-search-view', 'empty')
    this.toggleNoResultsMessage(false)

    this.predictiveGroups.forEach((group) => {
      group.hidden = true
    })

    this.emptyStates.forEach((state) => {
      state.hidden = false
    })

    this.emptyCards.forEach((cards) => {
      cards.hidden = false
    })

    this.resultStates.forEach((state) => {
      state.hidden = true
    })

    this.cardsContainers.forEach((container) => {
      container.hidden = true
      this.clearContainer(container)
    })

    this.suggestionLists.forEach((list) => {
      this.clearContainer(list)
    })

    this.collectionGroups.forEach((group) => {
      group.hidden = true
    })

    this.collectionLists.forEach((list) => {
      this.clearContainer(list)
    })
  }

  showResultState () {
    this.root.setAttribute('data-search-view', 'results')
    this.toggleNoResultsMessage(false)

    this.predictiveGroups.forEach((group) => {
      group.hidden = true
    })

    this.emptyStates.forEach((state) => {
      state.hidden = true
    })

    this.emptyCards.forEach((cards) => {
      cards.hidden = true
    })

    this.resultStates.forEach((state) => {
      state.hidden = false
    })

    this.cardsContainers.forEach((container) => {
      container.hidden = false
    })
  }

  renderResults (response) {
    const results = response && response.resources && response.resources.results
      ? response.resources.results
      : {}
    const products = Array.isArray(results.products) ? results.products : []
    const collections = Array.isArray(results.collections) ? results.collections : []
    const suggestionItems = this.buildSuggestionItems(results)
    const collectionItems = collections
      .map((item) => ({
        url: this.getResourceUrl(item),
        title: this.getResourceTitle(item)
      }))
      .filter((item) => item.url && item.title)
    const cardItems = products
      .filter((product) => this.getResourceUrl(product) && this.getResourceImage(product))

    if (collectionItems.length === 0 && cardItems.length === 0 && suggestionItems.length === 0) {
      this.showEmptyState()
      this.toggleNoResultsMessage(true)
      return
    }

    this.showResultState()

    this.collectionGroups.forEach((group) => {
      group.hidden = collectionItems.length === 0
    })

    this.predictiveGroups.forEach((group) => {
      group.hidden = suggestionItems.length === 0
    })

    this.collectionLists.forEach((list) => {
      this.clearContainer(list)
      collectionItems.forEach((item) => {
        list.appendChild(this.createSuggestionItem(item))
      })
    })

    this.suggestionLists.forEach((list) => {
      this.clearContainer(list)
      suggestionItems.forEach((item) => {
        list.appendChild(this.createSuggestionItem(item))
      })
    })

    this.cardsContainers.forEach((container) => {
      const label = container.querySelector('.redesign-search__label')

      this.clearContainer(container)

      if (label) {
        container.appendChild(label)
      }

      cardItems.forEach((item) => {
        container.appendChild(this.createCard(item))
      })
    })
  }

  toggleNoResultsMessage (isVisible) {
    this.noResultsMessages.forEach((message) => {
      message.hidden = !isVisible
    })
  }

  buildSuggestionItems (results) {
    const seen = {}
    const sources = [
      Array.isArray(results.pages) ? results.pages : [],
      Array.isArray(results.articles) ? results.articles : [],
      Array.isArray(results.collections) ? results.collections : []
    ]
    const items = sources.reduce((accumulator, source) => accumulator.concat(source), [])

    if (items.length > 0) {
      return items.reduce((accumulator, item) => {
        const url = this.getResourceUrl(item)
        const title = this.getResourceTitle(item)

        if (!url || !title || seen[url]) return accumulator

        seen[url] = true
        accumulator.push({ url, title })
        return accumulator
      }, []).slice(0, 6)
    }

    const fallbackProducts = Array.isArray(results.products) ? results.products : []

    return fallbackProducts.reduce((accumulator, item) => {
      const url = this.getResourceUrl(item)
      const title = this.getResourceTitle(item)

      if (!url || !title || seen[url]) return accumulator

      seen[url] = true
      accumulator.push({ url, title })
      return accumulator
    }, []).slice(0, 6)
  }

  getResourceUrl (item) {
    if (!item) return ''
    if (typeof item.url === 'string') return item.url
    if (item.online_store_url) return item.online_store_url
    return ''
  }

  getResourceTitle (item) {
    if (!item || typeof item.title !== 'string') return ''
    return item.title.trim()
  }

  getResourceImage (item) {
    if (!item) return ''

    if (typeof item.image === 'string') {
      return item.image
    }

    if (item.image && typeof item.image.url === 'string') {
      return item.image.url
    }

    if (item.featured_image && typeof item.featured_image.url === 'string') {
      return item.featured_image.url
    }

    if (item.featured_image && typeof item.featured_image === 'string') {
      return item.featured_image
    }

    return ''
  }

  formatCardTitle (title) {
    return this.getResourceTitle({ title })
  }

  createSuggestionItem (item) {
    const listItem = document.createElement('li')
    const link = document.createElement('a')

    listItem.className = 'redesign-search__suggestion-item'
    link.className = 'redesign-search__suggestion-link'
    link.href = item.url
    link.textContent = item.title
    listItem.appendChild(link)

    return listItem
  }

  createCard (item) {
    const article = document.createElement('article')
    const link = document.createElement('a')
    const image = document.createElement('img')
    const title = document.createElement('span')
    const imageUrl = this.getResourceImage(item)

    article.className = 'redesign-search__card'
    link.className = 'redesign-search__card-link'
    link.href = this.getResourceUrl(item)
    image.className = 'redesign-search__card-image'
    image.src = imageUrl
    image.alt = this.formatCardTitle(this.getResourceTitle(item))
    image.loading = 'lazy'
    title.className = 'redesign-search__card-title'
    title.textContent = this.formatCardTitle(this.getResourceTitle(item))
    link.appendChild(image)
    link.appendChild(title)
    article.appendChild(link)

    return article
  }

  clearContainer (container) {
    if (container.tagName === 'UL') {
      container.textContent = ''
      return
    }

    Array.from(container.children).forEach((child) => {
      if (child.classList && child.classList.contains('redesign-search__label')) return

      child.remove()
    })
  }
}

export default (root) => {
  if (!root) return null

  const instance = new RedesignSearchOverlay(root)
  instance.init()
  return instance
}
